import React, { useEffect, useState } from 'react';
import { Button } from '../UI/Button';
import { inboxService } from '../../services/inboxService';
import {
  isValidWhatsAppPhone,
  maskWhatsAppPhone,
  normalizeWhatsAppPhoneDigits,
} from '../../services/validationService';
import {
  KEEP_EXISTING_SECRET_HINT,
  WhatsAppInboxConfiguration,
} from '../../types/inboxConfiguration';
import { classifyWhatsAppWebhookHealth, MetaWebhookHealthStatus } from '../../types/metaWebhook';
import { useToast } from '../UI/ToastProvider';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { WhatsAppMetaSetupInstructions } from './WhatsAppMetaSetupInstructions';

type HealthView =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: MetaWebhookHealthStatus }
  | { status: 'error'; message: string };

interface WhatsAppInboxConfigSectionProps {
  config: WhatsAppInboxConfiguration;
  onChange: (patch: Partial<WhatsAppInboxConfiguration>) => void;
  isEdit: boolean;
  inboxId?: string;
  occupiedPhoneDigits: string[];
  apiRootUrl: string;
}

export const WhatsAppInboxConfigSection: React.FC<WhatsAppInboxConfigSectionProps> = ({
  config,
  onChange,
  isEdit,
  inboxId,
  occupiedPhoneDigits,
  apiRootUrl,
}) => {
  const { addToast } = useToast();
  const [registeringWebhook, setRegisteringWebhook] = useState(false);
  const [health, setHealth] = useState<HealthView>({ status: 'idle' });
  const [phoneError, setPhoneError] = useState('');

  const hasStoredAccessToken = Boolean(config.hasAccessToken);

  const currentPhoneDigits = normalizeWhatsAppPhoneDigits(config.phoneNumber);
  const phoneIsDuplicate = Boolean(
    currentPhoneDigits && occupiedPhoneDigits.includes(currentPhoneDigits)
  );

  useEffect(() => {
    if (phoneIsDuplicate)
    {
      setPhoneError('Já existe uma caixa de entrada WhatsApp com este número de telefone.');
      return;
    }

    if (config.phoneNumber && !isValidWhatsAppPhone(config.phoneNumber))
    {
      setPhoneError('Informe um número válido com DDI (8 a 15 dígitos, ex.: +5511999999999).');
      return;
    }

    setPhoneError('');
  }, [config.phoneNumber, phoneIsDuplicate]);

  useEffect(() => {
    if (!isEdit || !inboxId)
    {
      return;
    }

    let cancelled = false;

    const loadHealth = async () => {
      setHealth({ status: 'loading' });
      try
      {
        const data = await inboxService.getMetaWebhookHealth(inboxId);
        if (!cancelled)
        {
          setHealth({ status: 'ok', data });
        }
      }
      catch (error)
      {
        if (!cancelled)
        {
          setHealth({
            status: 'error',
            message: apiErrorMessage(error, 'Não foi possível consultar o status do webhook na Meta.'),
          });
        }
      }
    };

    void loadHealth();

    return () => {
      cancelled = true;
    };
  }, [inboxId, isEdit]);

  const handleCopyVerifyToken = async () => {
    if (!config.verifyToken)
    {
      return;
    }

    try
    {
      await navigator.clipboard.writeText(config.verifyToken);
      addToast('Verify token copiado', 'success');
    }
    catch
    {
      addToast('Não foi possível copiar o verify token', 'error');
    }
  };

  const handleRegisterWebhook = async () => {
    if (!inboxId)
    {
      return;
    }

    setRegisteringWebhook(true);
    try
    {
      const result = await inboxService.registerMetaWebhook(inboxId);
      if (result.healthStatus)
      {
        setHealth({ status: 'ok', data: result.healthStatus });
      }

      if (result.success)
      {
        addToast(result.message || 'Webhook registrado na Meta', 'success');
      }
      else
      {
        addToast(result.error || result.message || 'Falha ao registrar o webhook na Meta.', 'error');
      }
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Falha ao registrar o webhook na Meta.'), 'error');
    }
    finally
    {
      setRegisteringWebhook(false);
    }
  };

  const connectionState = health.status === 'ok'
    ? classifyWhatsAppWebhookHealth(health.data)
    : null;

  const webhookUrl = (health.status === 'ok' && health.data.expectedUrl)
    ? health.data.expectedUrl
    : `${apiRootUrl}/api/webhooks/whatsapp`;

  const showMetaSetup = isEdit && (
    health.status === 'error'
    || connectionState === 'not_configured'
    || connectionState === 'needs_finish'
  );

  const showRegisterButton = showMetaSetup;

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-gray-800">WhatsApp (Meta Cloud API)</h2>

      <div>
        <label className="block text-sm text-gray-700 mb-1">ID da conta comercial (WABA) *</label>
        <input
          type="text"
          value={config.businessAccountId || ''}
          onChange={e => onChange({ businessAccountId: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder="123456789012345"
          required
        />
      </div>

      <div>
        <label className="block text-sm text-gray-700 mb-1">Access token</label>
        <input
          type="password"
          value={config.accessToken || ''}
          onChange={e => onChange({ accessToken: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder={isEdit && hasStoredAccessToken ? '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••' : undefined}
          autoComplete="new-password"
        />
        <p className="text-xs text-gray-500 mt-1">
          {isEdit && hasStoredAccessToken
            ? KEEP_EXISTING_SECRET_HINT
            : 'Necessário para registrar o webhook na Meta.'}
        </p>
      </div>

      <div>
        <label className="block text-sm text-gray-700 mb-1">Phone Number ID</label>
        <input
          type="text"
          value={config.phoneNumberId || ''}
          onChange={e => onChange({ phoneNumberId: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-700 mb-1">Número do WhatsApp *</label>
        <input
          type="text"
          value={maskWhatsAppPhone(config.phoneNumber || '')}
          onChange={e => onChange({ phoneNumber: maskWhatsAppPhone(e.target.value) })}
          className={`w-full border rounded-md px-3 py-2 text-sm ${phoneError ? 'border-red-400' : 'border-gray-300'}`}
          placeholder="+5511999999999"
          required
        />
        {phoneError ? (
          <p className="text-xs text-red-600 mt-1">{phoneError}</p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">
            Único por cliente. Informe o número com DDI (ex.: +5511999999999 ou +5491112345678).
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm text-gray-700">
            Verify token {isEdit ? '' : '(opcional)'}
          </label>
          {config.verifyToken && (
            <Button variant="secondary" size="sm" type="button" onClick={() => void handleCopyVerifyToken()}>
              Copiar
            </Button>
          )}
        </div>
        <input
          type="text"
          readOnly={isEdit && Boolean(config.verifyToken)}
          value={config.verifyToken || ''}
          onChange={e => onChange({ verifyToken: e.target.value })}
          className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${isEdit && config.verifyToken ? 'bg-gray-50' : ''}`}
        />
        {!isEdit && (
          <p className="text-xs text-gray-500 mt-1">
            Se já configurou o webhook no painel da Meta, informe o mesmo verify token usado lá.
            Deixe em branco para gerar automaticamente — nesse caso, configure na Meta depois com o token gerado.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm text-gray-700 mb-1">App secret</label>
        <input
          type="password"
          value={config.appSecret || ''}
          onChange={e => onChange({ appSecret: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder={isEdit && config.hasAppSecret ? '••••••••••••••••••••••••••••••••' : undefined}
          autoComplete="new-password"
        />
        {isEdit && config.hasAppSecret && (
          <p className="text-xs text-gray-500 mt-1">{KEEP_EXISTING_SECRET_HINT}</p>
        )}
      </div>

      {isEdit && (
        <div className="border border-gray-200 rounded-md p-4 bg-gradient-to-br from-gray-50 to-gray-100 space-y-4">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-800">Status da Conexão</h3>
          </div>

          {health.status === 'loading' && (
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-md p-3">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              Verificando conexão com a Meta...
            </div>
          )}

          {health.status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-red-900">Não foi possível verificar o status</p>
                  <p className="text-xs text-red-700 mt-1">{health.message}</p>
                </div>
              </div>
            </div>
          )}

          {health.status === 'ok' && connectionState === 'synced' && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900">Tudo certo!</p>
                  <p className="text-xs text-green-700 mt-1">
                    Seu canal está conectado e pronto para receber mensagens do WhatsApp.
                  </p>
                </div>
              </div>
            </div>
          )}

          {health.status === 'ok' && connectionState === 'not_configured' && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">Configuração pendente</p>
                  <p className="text-xs text-amber-800 mt-1">
                    Copie os dados abaixo e complete a configuração no painel da Meta.
                  </p>
                </div>
              </div>
            </div>
          )}

          {health.status === 'ok' && connectionState === 'needs_finish' && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">Configuração pendente</p>
                  <p className="text-xs text-amber-800 mt-1">
                    Clique em <strong>Finalizar configuração</strong> para este canal começar a receber as mensagens aqui.
                  </p>
                </div>
              </div>
            </div>
          )}

          {showMetaSetup && (
            <WhatsAppMetaSetupInstructions
              webhookUrl={webhookUrl}
              verifyToken={config.verifyToken || ''}
            />
          )}

          {showRegisterButton && (
            <div className="pt-2">
              <Button
                variant="primary"
                size="sm"
                type="button"
                disabled={registeringWebhook}
                onClick={() => void handleRegisterWebhook()}
                className="w-full sm:w-auto"
              >
                {registeringWebhook ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Configurando...
                  </span>
                ) : (
                  'Finalizar configuração'
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
